import React, { useEffect, useState } from 'react'
import ToastMessage from '../../../../common/ToastMessage';
import CustomDataGrid from '../../../../common/CustomDataGrid';
import usePriorities from '../../../../store/ReactQueries/priorities/usePriority';
import useAddPriorities from '../../../../store/ReactQueries/priorities/useAddPriorities';
import useUpdatePriority from '../../../../store/ReactQueries/priorities/useUpdatePriority';
import useDeletePriority from '../../../../store/ReactQueries/priorities/useDeletePriority';
import { GridColDef } from '@mui/x-data-grid';
import { PAGESIZE } from '../../../../utils/constants';
import { FieldErrors } from '../../../../domain/helpers/Errors';
import { formatDate } from '../../../../common/GlobalFunctions';
import { validateSchema } from '../../../../common/ValidateSchema';
import { useDarkModeStore } from '../../../../store/useDarkModeStore';
import { useLanguageStore } from '../../../../store/useLanguageStore';
import { PriorityInterface } from '../../../../models/TicketModule/Priority';
import { prioritySchema } from '../../../../domain/ValidationSchemas/Ticket/priority';

export const Priorities = () => {
    const { language } = useLanguageStore();
    const { darkMode } = useDarkModeStore();

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(PAGESIZE);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [form, setForm] = useState<PriorityInterface>({
        id: '',
        title: '',
        isDefault: false,
    });

    const [modal, setModal] = useState<{ add: boolean, edit: boolean, view: boolean }>({
        add: false,
        edit: false,
        view: false
    });

    let { data: customers, isLoading, error: priorityError, refetch } = usePriorities(page, pageSize);

    const addPriory = useAddPriorities(page, pageSize);
    const updatePriority = useUpdatePriority(page, pageSize);
    const deletePriority = useDeletePriority(page, pageSize);

    useEffect(() => {
        if (priorityError) ToastMessage.Failed(`Error: ${priorityError.message}`);
    }, [priorityError]);

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Id', type: "string", editable: false, width: 150 },
        { field: 'title', headerName: 'Title', type: "string", editable: true, width: 200 },
        {
            field: 'isDefault',
            headerName: 'IsDefault',
            type: 'boolean',
            editable: true,
            renderCell: ({ value }) => (
                <span style={{ color: darkMode ? 'white' : 'black' }}>
                    {value ? `✓` : '✕'}
                </span>
            ),
        },
        { field: 'createdDate', headerName: 'CreatedDate', type: "dateTime", editable: false, width: 150, valueFormatter: formatDate },
        { field: 'updatedDate', headerName: 'UpdatedDate', type: "dateTime", editable: false, width: 150, valueFormatter: formatDate },
        { field: 'tenantId', headerName: 'TenantId', type: "string", editable: false, width: 150 },
        { field: 'authorId', headerName: 'AuthorId', type: "string", editable: false, width: 150 },
        { field: 'modifiedById', headerName: 'ModifiedById', type: "string", editable: false, width: 150 },
    ];

    const addItems = () => {
        setForm({ ...form, title: '', id: '', isDefault: false });
        setModal({ ...modal, add: true });
        setErrors({});
    }

    const handleError = (error: unknown) => {
        if (error instanceof Error) {
            console.log(error.message);
            ToastMessage.Failed(error.message);
        } else {
            console.log("An unknown error occurred");
            ToastMessage.Failed();
        }
    };

    const handleSave = async () => {
        const isValid = validateSchema(prioritySchema, form);

        setErrors(isValid.errors);
        if (!isValid.result) return;

        const upForm: PriorityInterface = {
            id: form.id,
            title: form.title,
            isDefault: form.isDefault
        };

        if (upForm.id === '') {
            const { id, ...formData } = upForm;
            addPriory.mutate(formData, {
                onSuccess: () => {
                    if (upForm.isDefault) refetch();
                    ToastMessage.Success();
                },
                onError: (error) => handleError(error),
            });
        } else {

            updatePriority.mutate(upForm, {
                onSuccess: () => {
                    if (upForm.isDefault) refetch();
                    ToastMessage.Success();
                },
                onError: (error) => handleError(error),
            });
        }
        setModal({ ...modal, add: false, edit: false, view: false });
    }

    const handleDelete = async (id: string) => {
        if (await ToastMessage.Confirm(language['ToastDeleteItem'])) {
            deletePriority.mutate(id, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        }
    };

    return (
        <CustomDataGrid
            name={language['Priorities']}
            rows={customers || []}
            showColumnModal={['title', 'isDefault']}
            hideColumns={['authorId', 'id', 'tenantId', 'modifiedById']}
            form={form}
            page={page}
            modal={modal}
            errors={errors}
            columns={columns}
            loading={isLoading}
            pageSize={pageSize}
            setForm={setForm}
            setPage={setPage}
            setModal={setModal}
            addItems={addItems}
            setErrors={setErrors}
            handleSave={handleSave}
            setPageSize={setPageSize}
            handleDelete={handleDelete}
        />
    );
}
export default Priorities;